import { BaseModel } from '../../../global/models';

export enum Settings {
  PAGE_SIZE = "pageSize",
  PAPER_WEIGHT = "paperWeight",
  PAGE_ORIENTATION = "pageOrientation",
  COLOR = "color",
  PAGES_PER_SHEET = "pagesPerSheet",
  TWO_SIDED = "twoSided",
  PAGE_TURN = "pageTurn",
  FINISHING = "finishing",
  GROUPED = "grouped",
  COVER_COLOR = "coverColor",
  COVER_LAMINATED = "coverLaminated",
  RING_COLOR = "ringColor",
  HARD_COVER_FRONT = "hardCoverFront", // new
  HARD_COVER_BACK = "hardCoverBack",  // new
  DOC_COLOR = 'docColor',
  AUTO_ROTATE = 'autoRotate',
  SKIP_FIT_TO_PAGE = 'skipFitToPage',
  LAMINATION_WEIGHT = 'laminationWeight',
  LAMINATION_TYPE = 'laminationType'
}

export interface PrintSettings {
  //copies: number; // total copies of this same document. > 0. default: 1
  [Settings.PAGE_SIZE]: PageSize;  // default: A4
  [Settings.PAPER_WEIGHT]: PaperWeight;
  [Settings.PAGE_ORIENTATION]: PageOrientation;  // default: landscape
  [Settings.COLOR]: PrintColor;  // default: bn
  [Settings.PAGES_PER_SHEET]: PagesPerSheet; // default: 1
  [Settings.TWO_SIDED]: boolean; // print on both sides. default: true
  [Settings.AUTO_ROTATE]: boolean; // print on both sides. default: true
  [Settings.PAGE_TURN]: PageTurn;
  [Settings.FINISHING]: Finishing;
  [Settings.GROUPED]: boolean;
  [Settings.COVER_COLOR]: boolean;
  [Settings.COVER_LAMINATED]: boolean;
  [Settings.RING_COLOR]: any;
  [Settings.HARD_COVER_FRONT]: any;
  [Settings.HARD_COVER_BACK]: any;
  [Settings.DOC_COLOR]: boolean;
  [Settings.SKIP_FIT_TO_PAGE]: boolean;
  [Settings.LAMINATION_TYPE]: LaminationType;
  [Settings.LAMINATION_WEIGHT]: LaminationWeight;
}

export interface PrintSettingsConfig extends BaseModel {
  autoRotate: PrintSettingGroup<boolean>;
  color: PrintSettingGroup<PrintColor>;
  coverColor: PrintSettingGroup<boolean>;
  coverLaminated: PrintSettingGroup<boolean>;
  docColor: PrintSettingGroup<boolean>;
  finishing: PrintSettingGroup<Finishing>;
  grouped: PrintSettingGroup<boolean>;
  hardCoverBack: PrintSettingGroup;
  hardCoverFront: PrintSettingGroup;
  laminationType: PrintSettingGroup<LaminationType>;
  laminationWeight: PrintSettingGroup<LaminationWeight>;
  pageOrientation: PrintSettingGroup<PageOrientation>;
  pageSize: PrintSettingGroup<PageSize>;
  pageTurn: PrintSettingGroup<PageTurn>;
  pagesPerSheet: PrintSettingGroup<PagesPerSheet>;
  paperWeight: PrintSettingGroup<PaperWeight>;
  ringColor: PrintSettingGroup;
  skipFitToPage: PrintSettingGroup<boolean>;
  twoSided: PrintSettingGroup<boolean>;
}

interface PrintSettingGroup<T = unknown> {
  key?: string;
  title?: string;
  selected?: number;
  options?: PrintSettingOption<T>[];
  categorized?: boolean;
  tip?: string;
}

interface PrintSettingOption<T> {
  title?: string;
  subTitle?: string;
  value?: T;

  badge?: string;
  infoTooltip?: string;

  size?: string;
  sizeMobile?: string;

  image?: string;
  video?: string;
  lottie?: string;

  settingCategory?: SettingCategory;
  extraData?: {
    isBindingWithCoverLaminated?: boolean;
    [key: string]: unknown;
  };

  unavailableFor?: PrintSettingRestriction[];
  unavailableForGrouped?: PrintSettingRestriction[];
}

type PrintSettingRestriction =
  | {
    id: string;
    options: number[];
    disabledTooltip?: string;
  }
  | {
    properties: {
      id: string;
      options: number[];
    }[];
    disabledTooltip?: string;
  };

export enum PaperWeight { W80 = '80', W90 = '90', W100 = '100', W120 = '120', W160 = '160', W200 = '200', W300 = '300' }

export type TwoSided = boolean;

export enum LaminationType { Matte = 'matte', Gloss = 'gloss' }

export enum LaminationWeight { W80 = '80', W125 = '125', W150 = '150', W175 = '175', W250 = '250' }

export enum PageSize { A3 = 'A3', A4 = 'A4', A5 = 'A5' }

export enum PageOrientation { PORTRAIT = 'portrait', LANDSCAPE = 'landscape' }

export enum PrintColor { BLACKNWHITE = 'bn', COLOR = 'color' }

export enum PagesPerSheet { ONE = 1, TWO = 2, FOUR = 4 } // 6, 9, 16

export enum PageTurn { LARGESIDE = 'largeside', SHORTSIDE = 'shortside' }

export enum Finishing {
  NONE = 'none',
  STAPLED = 'stapled',
  BINDING = 'binding',
  BINDINGX = 'bindingx',
  BINDING_LAMINATED = 'bindingLaminated',
  LAMINATEDA4 = 'laminatedA4',
  LAMINATEDA3 = 'laminatedA3',
  LAMINATED = 'laminated',
  PERFORATED2 = 'perforated2',
  PERFORATED4 = 'perforated4',
  PERFORATEDBIND = 'perforatedbind',
} // BINDINGX only for backward compatibility in old orders and old cart sharing

export enum SettingCategory {
  BASIC = 'basic',
  BINDING = 'binding',
  NOBINDING = 'nobinding',
  OTHERS = 'others',
  SPECIAL = 'special'
}

export const SettingCategoryText: Record<SettingCategory, string> = {
  [SettingCategory.BASIC]: 'Acabados básicos',
  [SettingCategory.BINDING]: 'Acabados encuadernados',
  [SettingCategory.NOBINDING]: 'Acabados sin encuadernar',
  [SettingCategory.OTHERS]: 'Otros acabados',
  [SettingCategory.SPECIAL]: 'Acabados especiales'
};

